import {FILE_UPLOAD_OPERATION_ALL_FILES_DIALOG, useAppDispatch, useAppState} from "../app/AppContext";
import React, {lazy, Suspense, useCallback, useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import {cancelOperation} from "./cancelOperation";
import {clientLog} from "../clientLog";
import Typography from "@mui/material/Typography";
import {FileConversionStatus} from "./FileConversionStatus";
import {useDocumentTranslation} from "../documents/documentContext";
import CloseIcon from "@mui/icons-material/Close";
import {Stack} from "@mui/material";
import {
    getHrStatusDetails,
    getOperationLastUpdate,
    getOperationsByType,
    getProgress,
    isOperationDone,
    isWorkerMessagesPresent
} from "./operationsPopperFunctions";
import {useSnackbar} from "notistack";
import {recordFailureShown} from "./recordFailureShown";

// Lazy-loaded: DialogListFiles pulls in material-react-table, @tanstack/table-core
// and related metadata-editing forms (~450 kB unminified). OperationsPopper is
// mounted unconditionally in App.jsx, so a static import here previously forced
// all of that into the app's main entry bundle even though the dialog is only
// opened when a user clicks "Se igjennom" on a finished upload operation.
const DialogListFiles = lazy(() =>
    import("../damsfileupload3/metadata/DialogListFiles").then(m => ({default: m.DialogListFiles}))
);

const getDoneFileUploadOperations = (operations) =>
    getOperationsByType(operations || [], 'FILE_UPLOAD').filter(o => isOperationDone(o));

const _firstReport = (reported, key) => {
    if (reported.has(key)) {
        return false;
    }
    reported.add(key);
    return true;
};

/**
 * OperationsPopper component displays a popper with active file upload operations.
 * @returns {false|React.JSX.Element}
 * @constructor
 */
export const OperationsPopper = () => {
    const t = useDocumentTranslation();
    const {operations} = useAppState();
    const appDispatch = useAppDispatch();

    const {enqueueSnackbar} = useSnackbar();

    const [closedByUser, setClosedByUser] = useState(false);
    // Finished jobs that were already on screen when the user closed the list; they must not reopen it.
    const [dismissedJobIds, setDismissedJobIds] = useState([]);
    const [showActiveOperations, setShowActiveOperations] = useState(false);
    const [activeOperations, setActiveOperations] = useState([]);

    const [viewedFileOperations, setViewedFileOperations] = useState([]);

    // Persist timeout IDs across renders so they can be cleared reliably.
    const reopenTimeoutRef = useRef(null);   // 5-minute reopen timeout
    const resetClosedTimeoutRef = useRef(null); // 200 ms reset timeout
    // Jobs whose timeout or error was already reported, so a re-render never repeats the toast (DE-1816).
    const reportedRef = useRef(new Set());
    // Jobs whose cancel request is in flight, so a second "Lukk" click sends no second request.
    const cancellingRef = useRef(new Set());

    // Clear all pending timeouts when the component unmounts.
    useEffect(() => {
        return () => {
            clearTimeout(reopenTimeoutRef.current);
            clearTimeout(resetClosedTimeoutRef.current);
        };
    }, []);

    /**
     * Handles the click event for the "Vis alle filer" button.
     *
     * When the button is clicked, the `fileUploadOperationAllFilesDialog` action is dispatched with the operation ID and the `open` property set to `true`.
     * This will open the dialog showing all files for the operation.
     *
     * @private
     */
    const handleViewAllFilesClick = (operation, lastUpdate) => {
        if (!operation) {
            return;
        }
        const {jobId} = operation;
        if (!jobId) {
            return;
        }

        const failedConversion = lastUpdate?.details?.failed?.map(failed => (failed['dms_id'])) || [];
        setViewedFileOperations([...viewedFileOperations, jobId]);
        appDispatch({
            type: FILE_UPLOAD_OPERATION_ALL_FILES_DIALOG,
            data: {
                open: true,
                operationId: jobId,
                failedConversion: failedConversion
            }
        });
    };

    /**
     * Handles the click event for the "Lukk" button on the file upload operation error dialog.
     *
     * When the button is clicked, the cancel request is sent; the operation is removed only once it succeeds, otherwise an error toast is shown.
     * This will cancel the operation and remove it from the list of operations.
     *
     * @private
     */
    const handleErrorClick = (operation) => {
        const {jobId} = operation;
        if (cancellingRef.current.has(jobId)) {
            return;
        }
        cancellingRef.current.add(jobId);
        cancelOperation(appDispatch, operation, (cancelledJobId) => {
            cancellingRef.current.delete(jobId);
            clientLog('info', `operation: ${cancelledJobId} - cancelled by the user`, 'fileuploadoperationactionbuttons');
        }, (reason) => {
            cancellingRef.current.delete(jobId);
            clientLog('error', `operation: ${jobId} - cancel failed: ${reason}`, 'fileuploadoperationactionbuttons');
            enqueueSnackbar(t('fileUploadCancelFailedMessage', 'Kunne ikke lukke opplastingen. Prøv igjen.'), {variant: 'error'});
        });
    };

    /**
     * Toggles the visibility of the list of active operations.
     *
     * If the list is visible, this will hide it and vice versa.
     *
     * @private
     */
    const toggleOperationsList = () => {
        // Clear any pending reopen timeout so a second click does not cause
        // the popper to reopen unexpectedly after being manually closed.
        if (closedByUser) {
            clearTimeout(reopenTimeoutRef.current);
            reopenTimeoutRef.current = null;
        }

        if (showActiveOperations) {
            setClosedByUser(true);
            setShowActiveOperations(false);
            setDismissedJobIds(getDoneFileUploadOperations(operations).map(o => o.jobId));
            clearTimeout(reopenTimeoutRef.current);
            reopenTimeoutRef.current = setTimeout(() => {
                setShowActiveOperations(operations.length > 0);
                setClosedByUser(false);
                setDismissedJobIds([]);
                reopenTimeoutRef.current = null;
            }, 5 * 60 * 1000);
        }

        if (!showActiveOperations && !closedByUser) {
            setShowActiveOperations(true);
        } else {
            clearTimeout(resetClosedTimeoutRef.current);
            resetClosedTimeoutRef.current = setTimeout(() => {
                setClosedByUser(false);
                resetClosedTimeoutRef.current = null;
            }, 200);
        }
    };

    /**
     * Handles the case when a file upload operation times out.
     *
     * When a file upload operation times out, this function is called. It logs an error message to the console,
     * indicating that the operation timed out, and displays a snackbar with an error message.
     *
     * @function handleFileUploadTimeout
     */
    const handleFileUploadTimeout = (jobId, lastUpdate) => {
        clientLog('error', `${jobId} ${lastUpdate.id} timed out`, 'fileuploadoperation');
        enqueueSnackbar(t('fileUploadTimeoutMessage', 'Filopplastingen tar for lang tid - lukk den i listen'), {variant: 'error'});
    };

    /**
     * Handles the case when a file upload operation fails.
     *
     * When a file upload operation fails, this function is called with the job ID and the last update message.
     * The function logs an error message to the console, indicating that the operation failed, and displays a snackbar with an error message.
     * The operation is not cancelled: it stays in the list until the user closes it with "Lukk".
     *
     * @param {object} o - The operation that failed.
     * @param {object} lastUpdate - The last update message sent from the worker.
     * @param {object} details - The details of the failure.
     */
    const handleFileUploadError = (o, lastUpdate, details) => {
        const {jobId} = o;
        clientLog('error', `${jobId} ${lastUpdate.id} failed: ${JSON.stringify(details)}`, 'fileuploadoperation');
        enqueueSnackbar(t('fileUploadErrorMessage', 'Det oppstod en ukjent feil'), {variant: 'error'});
        recordFailureShown(jobId);
    };

    /**
     * Returns a list of JSX elements representing the file upload operations that are
     * in progress. Each element is a box that contains the number of files in the
     * operation, the status of the operation, and a progress bar representing the
     * progress of the operation. If the operation has timed out or failed, a 'Lukk' button is
     * displayed. If the operation is complete, a 'Se igjennom' button is displayed.
     *
     * @returns {JSX.Element} A list of JSX elements representing the file upload operations
     * that are in progress.
     */
    const getFileUploadOperations = () => {
        if (!operations || operations.length === 0) {
            return <></>;
        }

        const fileUploadOperations = getOperationsByType(operations, 'FILE_UPLOAD');
        if (fileUploadOperations.length === 0) {
            return <></>;
        }

        return <Box sx={{marginTop: '8px'}}>
            <Typography variant="caption" sx={{marginBottom: '4px'}}>
                {t('fileUploadUploadTitle', 'Opplasting')}
            </Typography>
            {fileUploadOperations.map((o, index) => {
                let allDone;
                const {
                    jobId,
                    files = [],
                    workerMessages
                } = o;
                const lastUpdate = getOperationLastUpdate(operations, workerMessages);
                const {timedOut = false, status, id} = lastUpdate;

                // We're only interested in the files successfully uploaded.
                const uploadedFiles = files.filter(f => f.status === 'ok');

                const hrStatusDetails = getHrStatusDetails(t, timedOut, lastUpdate, uploadedFiles);
                const failed = status === 'error';
                const progress = failed ? 0 : getProgress(uploadedFiles, lastUpdate);

                allDone = (progress === 100 && status === 'done' && id === 'gettingobjectstatus')
                    || (status === 'done' && id === 'operationcomplete');

                return <Box key={`upload-operation-${jobId}`} sx={{borderBottom: 'solid 1px #bbb', padding: '8px'}}>
                    <Box>
                        {hrStatusDetails.join(' ')}
                    </Box>
                    <Box>
                        <LinearProgress variant="determinate" value={allDone ? 100 : progress}
                                        aria-label={`${t('fileUploadUploadTitle', 'Opplasting')} ${index + 1}`}/>
                    </Box>
                    <Box>
                        <FileConversionStatus conversionDetails={lastUpdate} t={t}/>
                    </Box>
                    <Box>
                        {(timedOut || failed) && <Button key={`close-${jobId}`}
                                             onClick={(event) => {
                                                 event.stopPropagation();
                                                 handleErrorClick(o);
                                             }}>
                            {t('fileUploadBtnClose', 'Lukk')}
                        </Button>}
                        {allDone && <Button key={`view-all-files-${jobId}`}
                                            disabled={viewedFileOperations.includes(jobId)}
                                            onClick={() => handleViewAllFilesClick(o, lastUpdate)}>
                            {t('fileUploadBtnBrowse', 'Se igjennom')}
                        </Button>}
                    </Box>
                </Box>
            })}
        </Box>
    };

    /**
     * Check if at least one file upload operation is complete. If so, display list of operations.
     *
     * This function goes through all file upload operations and checks if any of them is complete.
     * If at least one is complete, then the list of operations is displayed.
     *
     * @returns {undefined}
     */
    const atLeastOneFileUploadOperationDone = useCallback(() => {
        if (!operations || !getOperationsByType(operations, 'FILE_UPLOAD').length) {
            return;
        }
        const newlyDone = getDoneFileUploadOperations(operations).some(o => !dismissedJobIds.includes(o.jobId));
        if (newlyDone && !showActiveOperations && !closedByUser) {
            setShowActiveOperations(true);
        }
    }, [operations, showActiveOperations, closedByUser, dismissedJobIds]);

    /**
     * Reports each job's timeout or error once per page load, outside the render (DE-1816).
     */
    useEffect(() => {
        getOperationsByType(operations || [], 'FILE_UPLOAD').forEach(o => {
            const lastUpdate = getOperationLastUpdate(operations, o.workerMessages);
            if (lastUpdate.timedOut && _firstReport(reportedRef.current, `timeout-${o.jobId}`)) {
                handleFileUploadTimeout(o.jobId, lastUpdate);
            }
            if (lastUpdate.status === 'error' && _firstReport(reportedRef.current, `error-${o.jobId}`)) {
                handleFileUploadError(o, lastUpdate, lastUpdate.details);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operations]);

    /**
     * Hook used to update the list of active operations.
     */
    useEffect(() => {
        if (!operations || operations.length === 0) {
            return;
        }
        atLeastOneFileUploadOperationDone();
        setActiveOperations(operations);
    }, [operations, atLeastOneFileUploadOperationDone]);

    // When worker messages are present for one or more operations, display the information box,
    // otherwise hide it!
    return isWorkerMessagesPresent(operations) &&
        <Box
            sx={{
                backgroundColor: 'white',
                border: 'solid 1px #bbb',
                borderRadius: '0px 0px 4px 4px',
                padding: '8px',
                cursor: 'pointer',
                position: 'absolute',
                top: '64px',
                right: '39px',
                zIndex: 999,
                minWidth: '300px'
            }}
            onClick={toggleOperationsList}>
            <Stack
                direction="row"
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                <Box>
                    {t('fileUploadActiveOperations', 'Aktive operasjoner')}: {activeOperations.length}
                </Box>
                {showActiveOperations && <CloseIcon color="secondary"/>}
            </Stack>
            {showActiveOperations && getFileUploadOperations()}
            <Suspense fallback={null}>
                <DialogListFiles/>
            </Suspense>
        </Box>;
};